import { ACS_TOP } from "./assumptions.ts";
import type { Metric } from "./types.ts";

/**
 * Source credits, in the words the sources ask for (2026-10-06 licence review): Zillow's terms require "Data Provided by
 * Zillow Group" wherever its numbers show (no logo); Realtor.com asks for its name and a link to its data library;
 * Freddie Mac for PMMS. Every source with its notices: the About page.
 */
export const DATA_CREDIT = "Data Provided by Zillow Group · Realtor.com® Economic Research · Redfin · U.S. Census Bureau · Freddie Mac PMMS® · FEMA";
/** The map's short version with links (HTML attribution; the sidebar footer and About carry the full list) */
export const DATA_CREDIT_HTML = [
  `<a href="https://www.zillow.com/research/data/" target="_blank" rel="noopener">Data Provided by Zillow Group</a>`,
  `<a href="https://www.realtor.com/research/data/" target="_blank" rel="noopener">Realtor.com®</a>`,
  `<a href="https://www.redfin.com/news/data-center/" target="_blank" rel="noopener">Redfin</a>`,
  `<a href="#/about">more</a>`,
].join(" · ");

const compactUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const int = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const dec = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** Format a metric value for display. `short` is for legends and axes; `digits` forces decimals for pct/ratio. */
const ACS_TOP_CODES = new Set(Object.values(ACS_TOP));

export function fmt(m: Pick<Metric, "unit">, v: number | null | undefined, short = false, digits?: number): string {
  if (v == null || !Number.isFinite(v)) return "–";
  switch (m.unit) {
    case "usd":
      // a Census median at its top code means "this or more" ($250,000+ income, $10,000+ taxes, $2M+ value)
      if (ACS_TOP_CODES.has(v)) return `${short || v - 1 >= 1e6 ? compactUsd.format(v - 1) : usd.format(v - 1)}+`;
      return short || Math.abs(v) >= 1e6 ? compactUsd.format(v) : usd.format(v);
    case "usd_month":
      return `${short ? compactUsd.format(v) : usd.format(v)}/mo`;
    case "pct": {
      const p = v * 100;
      const d = digits ?? (Math.abs(p) < 10 ? 1 : 0);
      return `${p.toFixed(d)}%`;
    }
    case "pct1":
      return `${(v * 100).toFixed(digits ?? 1)}%`;
    case "months":
      return `${v.toFixed(digits ?? 1)}${short ? " mo" : " months"}`;
    case "count":
      return short && Math.abs(v) >= 1e4 ? Intl.NumberFormat("en-US", { notation: "compact" }).format(v) : int.format(v);
    case "days":
      return `${int.format(v)}${short ? "d" : " days"}`;
    case "score":
      return int.format(v);
    case "per_sqmi":
      return short ? Intl.NumberFormat("en-US", { notation: "compact" }).format(v) : `${int.format(v)} /sq mi`;
    case "degf":
      return `${int.format(v)}°F`;
    default:
      return digits != null ? v.toFixed(digits) : dec.format(v);
  }
}

/** A share as a percent: 0.052 → "5.2%"; none → "–". */
export const pct = (v: number | null | undefined, digits = 1) => (v == null || !Number.isFinite(v) ? "–" : `${(v * 100).toFixed(digits)}%`);
/** A change as a signed percent with a true minus: 0.052 → "+5.2%", −0.03 → "−3.0%"; one that rounds to zero has no sign. */
export function pctChange(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "–";
  const a = Math.abs(v * 100).toFixed(digits);
  return `${Number(a) === 0 ? "" : v > 0 ? "+" : "−"}${a}%`;
}

/** Text made safe inside HTML (element content and quoted attributes). */
export const escHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Where a city's ZIP-built number comes from, in one sentence (panel, market page): "Built from its 3 ZIP codes, each
 * counted by its homes in Mill Valley: 47% of that data is about Mill Valley itself, so read it as rough."
 */
export function zipBuiltNote(g: { name: string; zips?: number; zip_fit?: number | null; zip_rough?: boolean }): string {
  const city = g.name.replace(/, [A-Z]{2}$/, "");
  const n = g.zips ?? 0;
  return `Built from its ${n > 1 ? `${n} ZIP codes, each` : "ZIP code,"} counted by its homes in ${city}: ${Math.round((g.zip_fit ?? 0) * 100)}% of that data is about ${city} itself${g.zip_rough ? ", so read it as rough" : ""}.`;
}

/** The label for a city number from its ZIPs, or one that mixes the city's own figures with ZIP-built ones. */
export function zipOrigin(g: { level: string; from_zips?: string[] | null; partly_zips?: string[] | null; zip_rough?: boolean }, id: string): { label: string; partly: boolean } | null {
  if (g.level !== "city") return null;
  const full = !!g.from_zips?.includes(id), partly = !full && !!g.partly_zips?.includes(id);
  if (!full && !partly) return null;
  return { label: `${g.zip_rough ? "rough: " : ""}${partly ? "partly " : ""}from its ZIPs`, partly };
}

/** zipBuiltNote, led by what a mixed number combines. */
export function zipOriginNote(g: Parameters<typeof zipBuiltNote>[0], partly: boolean): string {
  return `${partly ? "Combines the city's own figures with ones built from its ZIPs. Those are b" : "B"}${zipBuiltNote(g).slice(1)}`;
}

export function periodLabel(p: string | null | undefined): string {
  if (!p) return "–";
  const [y, m] = p.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/** ACS 5-year estimates (not the ACS microdata the insurance model uses): stored as Jan 1 of the survey's last year */
export const isAcs5 = (m: { cadence: string; source: string }) => m.cadence === "yearly" && /^Census ACS(?! microdata)/.test(m.source);

/**
 * When a metric's value is from: the month for monthly data, the year for yearly data, and the five survey years for
 * the ACS ("2020–24 survey"): its Jan 1 period read as "Jan 2024" looked like a stale month.
 */
export function metricPeriod(m: { cadence: string; source: string }, p: string | null | undefined): string {
  if (!p) return "–";
  if (m.cadence !== "yearly") return periodLabel(p);
  const y = Number(p.slice(0, 4));
  return isAcs5(m) ? `${y - 4}–${String(y).slice(2)} survey` : String(y);
}

export const LEVEL_LABEL: Record<string, string> = { us: "National", state: "State", metro: "Metro", county: "County", zip: "ZIP", city: "City" };
export const LEVEL_PLURAL: Record<string, string> = { us: "National", state: "States", metro: "Metros", county: "Counties", zip: "ZIPs", city: "Cities" };
